import React, { useState, useEffect, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import api from '../../components/api';
import Loader from '../Loader/Loader';
import './Compatibility.css';
import Navbar from '../Page/Navbar';
import { Helmet } from 'react-helmet';

const Compatibility = () => {
    const { guid } = useParams();
    const [loadingState, setLoadingState] = useState('idle');
    const [error, setError] = useState(null);
    const [compatibilityData, setCompatibilityData] = useState(null);
    const [citySuggestions1, setCitySuggestions1] = useState([]);
    const [citySuggestions2, setCitySuggestions2] = useState([]);
    const [showSuggestions1, setShowSuggestions1] = useState(false);
    const [showSuggestions2, setShowSuggestions2] = useState(false);
    const [citySearch1, setCitySearch1] = useState('');
    const [citySearch2, setCitySearch2] = useState('');
    const [formData, setFormData] = useState({
        person1: {
            birth_date: '',
            birth_time: '',
            city: '',
            latitude: '',
            longitude: ''
        },
        person2: {
            birth_date: '',
            birth_time: '',
            city: '',
            latitude: '',
            longitude: ''
        }
    });

    // Textes pour le SEO
    const seoContent = {
        faq: [
            {
                question: "La compatibilité est-elle déterminée par les signes ou les ascendants ?",
                answer: "Les deux sont importants, mais une synastrie complète analyse bien plus : positions planétaires, aspects entre les deux thèmes, placements en maisons. Le signe solaire n'est qu'un facteur parmi de nombreux indicateurs."
            },
            {
                question: "Peut-on être astrologiquement incompatibles et vivre une relation heureuse ?",
                answer: "Absolument. L'astrologie révèle des tendances et potentiels, pas des destinées figées. La conscience des défis peut même renforcer une relation. Le libre arbitre et l'évolution personnelle transcendent toujours les prédispositions astrologiques."
            },
            {
                question: "Quelle est la différence entre synastrie et thème composite ?",
                answer: "La synastrie compare deux thèmes distincts pour analyser leurs interactions. Le thème composite crée un nouveau thème 'fusionné' représentant la relation comme entité unique. Les deux approches sont complémentaires pour une analyse relationnelle complète."
            }
        ]
    };

    // Composant pour l'article informatif sur la synastrie
    const SynastrieInfoContent = () => (
        <div className="article panel">
            <h2>La synastrie, c'est quoi ?</h2>
            <p>
                La synastrie est une branche spécialisée de l'astrologie qui étudie les interactions entre les thèmes astraux de deux personnes. Plus qu'un simple test de compatibilité, elle révèle la dynamique énergétique complexe qui se crée lorsque deux cartes du ciel entrent en relation.
            </p>
            <p>
                Cette analyse approfondie examine comment les planètes d'une personne forment des aspects (angles) avec les planètes de l'autre, créant ainsi des résonances harmonieuses ou des tensions dynamiques. Chaque relation possède sa propre "empreinte astrologique" unique, aussi complexe et nuancée que les individus qui la composent.
            </p>
            <p>
                Contrairement aux simplifications populaires qui ne considèrent que les signes solaires, la synastrie professionnelle prend en compte l'ensemble des facteurs astrologiques : positions planétaires, maisons, aspects, points sensibles et plus encore. C'est cette vision holistique qui permet une compréhension véritable des potentiels relationnels.
            </p>

            <h2>Comment calculons-nous votre compatibilité ?</h2>
            <p>
                Notre calcul de compatibilité astrologique repose sur une méthodologie en plusieurs étapes, combinant l'astrologie traditionnelle et les approches modernes pour une analyse précise et nuancée.
            </p>
            <p>
                Premièrement, nous établissons les <Link to="/calcul-theme-astral">thèmes natals complets</Link> des deux personnes, en utilisant leurs données de naissance exactes (date, heure, lieu). Ces positions planétaires fondent toute l'analyse. Ensuite, notre algorithme identifie tous les aspects interplanétaires entre les deux thèmes - conjonctions, trigones, carrés, oppositions et autres angles significatifs.
            </p>
            <p>
                Nous accordons une attention particulière aux interactions entre planètes personnelles : <Link to="/blog/planetes/soleil">Soleil</Link>-<Link to="/blog/planetes/lune">Lune</Link> (identité et émotions), <Link to="/blog/planetes/venus">Vénus</Link>-<Link to="/blog/planetes/mars">Mars</Link> (attraction et passion), <Link to="/blog/planetes/mercure">Mercure</Link> (communication), ainsi qu'aux aspects impliquant l'Ascendant et les angles des maisons. Notre système pondère également l'importance des aspects selon leur nature, précision et planètes impliquées.
            </p>
            <p>
                Le résultat final est un score global et des analyses sectorielles qui révèlent non seulement le potentiel d'harmonie, mais aussi les zones de croissance et d'évolution au sein de la relation.
            </p>
        </div>
    );

    // Composant pour la compréhension des scores
    const ScoreGuide = () => {
        // Fonction utilitaire pour créer des liens vers les planètes
        const getPlanetLink = (planetName, plural = false) => {
            const planetMap = {
                'Soleil': '/blog/planetes/soleil',
                'Lune': '/blog/planetes/lune',
                'Mercure': '/blog/planetes/mercure',
                'Vénus': '/blog/planetes/venus',
                'Mars': '/blog/planetes/mars',
                'Jupiter': '/blog/planetes/jupiter',
                'Saturne': '/blog/planetes/saturne',
                'Uranus': '/blog/planetes/uranus',
                'Neptune': '/blog/planetes/neptune',
                'Pluton': '/blog/planetes/pluton',
            };
            
            const baseName = planetName.replace(/s$/, ''); // Enlève le 's' éventuel
            const displayName = plural ? `${planetName}s` : planetName;
            
            return planetMap[baseName] ? 
                <Link to={planetMap[baseName]}>{displayName}</Link> : 
                displayName;
        };
        
        return (
            <div className="interpretation panel">
                <h2>Comprendre votre score de compatibilité</h2>
                
                <h3>Attraction Mutuelle</h3>
                <p>
                    Ce score évalue l'alchimie naturelle et instinctive entre deux personnes. Il analyse principalement les liens entre vos Ascendants (première impression et attirance physique), vos {getPlanetLink('Soleil', true)} (ego et identité) et vos {getPlanetLink('Lune', true)} (besoins émotionnels). Une forte compatibilité ici indique une connexion immédiate et intuitive.
                </p>
                
                <h3>Dynamiques Relationnelles</h3>
                <p>
                    Cette section examine comment vous interagissez dans la relation quotidienne. Les positions de {getPlanetLink('Vénus')} (amour, affection, valeurs) et {getPlanetLink('Mars')} (passion, énergie, désir) révèlent votre façon d'aimer et d'exprimer vos sentiments. Ces aspects déterminent la fluidité de vos échanges émotionnels et physiques.
                </p>
                
                <h3>Aspects Majeurs</h3>
                <p>
                    Les aspects interplanétaires sont les angles formés entre les planètes de vos thèmes respectifs. Certaines configurations particulièrement puissantes, comme les liens avec le Descendant (relations engagées) ou les aspects {getPlanetLink('Pluton')}-{getPlanetLink('Lune')} (transformation émotionnelle profonde), peuvent indiquer une relation karmique ou transformative.
                </p>
                
                <h3>Défis Clés</h3>
                <p>
                    Aucune relation n'est sans tensions. Cette section identifie les zones potentielles de friction - communication, besoins émotionnels, équilibre entre autonomie et fusion. Cependant, ces défis ne sont pas nécessairement négatifs : ils peuvent constituer d'importantes opportunités de croissance personnelle et relationnelle.
                </p>
            </div>
        )
    };

    // Composant pour la FAQ
    const FAQ = () => {
        // Fonction utilitaire pour créer des liens vers les signes
        const getSignLink = (signName) => {
            const signMap = {
                'Bélier': '/blog/signes/belier',
                'Taureau': '/blog/signes/taureau',
                'Gémeaux': '/blog/signes/gemeaux',
                'Cancer': '/blog/signes/cancer',
                'Lion': '/blog/signes/lion',
                'Vierge': '/blog/signes/vierge',
                'Balance': '/blog/signes/balance',
                'Scorpion': '/blog/signes/scorpion',
                'Sagittaire': '/blog/signes/sagittaire',
                'Capricorne': '/blog/signes/capricorne',
                'Verseau': '/blog/signes/verseau',
                'Poissons': '/blog/signes/poissons'
            };
            
            return signMap[signName] ? <Link to={signMap[signName]}>{signName}</Link> : signName;
        };
        
        // Version enrichie des questions FAQ avec des liens
        const enrichedFaq = [
            {
                question: "La compatibilité est-elle déterminée par les signes ou les ascendants ?",
                answer: <>Les deux sont importants, mais une synastrie complète analyse bien plus : positions planétaires, aspects entre les deux thèmes, placements en maisons. Le <Link to="/blog/planetes/soleil">signe solaire</Link> n'est qu'un facteur parmi de nombreux indicateurs.</>
            },
            {
                question: "Peut-on être astrologiquement incompatibles et vivre une relation heureuse ?",
                answer: <>Absolument. L'astrologie révèle des tendances et potentiels, pas des destinées figées. La conscience des défis peut même renforcer une relation. Le libre arbitre et l'évolution personnelle transcendent toujours les prédispositions astrologiques.</>
            },
            {
                question: "Quelle est la différence entre synastrie et thème composite ?",
                answer: <>La synastrie compare deux <Link to="/calcul-theme-astral">thèmes</Link> distincts pour analyser leurs interactions. Le thème composite crée un nouveau thème 'fusionné' représentant la relation comme entité unique. Les deux approches sont complémentaires pour une analyse relationnelle complète.</>
            },
            {
                question: "Quels sont les couples de signes traditionnellement compatibles ?",
                answer: <>Plusieurs combinaisons sont reconnues pour leur harmonie naturelle : {getSignLink('Bélier')} et {getSignLink('Lion')} partagent une énergie de feu dynamique, {getSignLink('Taureau')} et {getSignLink('Cancer')} une sensibilité terrestre et émotionnelle, {getSignLink('Gémeaux')} et {getSignLink('Balance')} une connexion intellectuelle aérienne, et {getSignLink('Scorpion')} et {getSignLink('Poissons')} une profondeur intuitive. Mais n'oubliez pas que la synastrie complète va bien au-delà des seuls signes solaires.</>
            }
        ];
        
        return (
            <div className="faq panel">
                <h2>Questions fréquentes sur la compatibilité astrologique</h2>
                
                <div className="faq-items" itemScope itemType="https://schema.org/FAQPage">
                    {enrichedFaq.map((item, index) => (
                        <div key={index} className="faq-item" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
                            <details>
                                <summary itemProp="name">{item.question}</summary>
                                <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                                    <p itemProp="text">{item.answer}</p>
                                </div>
                            </details>
                        </div>
                    ))}
                </div>
                
                <div className="additional-resources" style={{ marginTop: "20px", paddingTop: "15px", borderTop: "1px solid #eaeaea" }}>
                    <h3>Ressources supplémentaires</h3>
                    <p>Pour une analyse approfondie de votre profil astrologique individuel, consultez notre <Link to="/calcul-theme-astral">calculateur de thème astral complet</Link>.</p>
                </div>
            </div>
        );
    };

    // Schema.org pour l'outil de calcul
    const getToolSchema = () => {
        return {
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "Calculateur de Compatibilité Astrologique",
            "applicationCategory": "Astrologie",
            "operatingSystem": "Web",
            "offers": {
                "@type": "Offer",
                "price": "0",
                "priceCurrency": "EUR"
            },
            "description": "Découvrez votre compatibilité astrologique avec notre outil gratuit d'analyse de synastrie complète."
        };
    };

    // Schema.org pour les compatibilités astrologiques
    // const getPopularPairsSchema = () => {
    //     return {
    //         "@context": "https://schema.org",
    //         "@type": "ItemList",
    //         "name": "Compatibilités astrologiques populaires",
    //         "description": "Les combinaisons de signes astrologiques les plus consultées pour la compatibilité amoureuse",
    //         "itemListElement": [
    //             {
    //                 "@type": "ListItem",
    //                 "position": 1,
    //                 "name": "Compatibilité Lion et Bélier",
    //                 "url": "https://astralight.xyz/compatibilite-astrologique"
    //             },
    //             {
    //                 "@type": "ListItem",
    //                 "position": 2,
    //                 "name": "Compatibilité Taureau et Cancer",
    //                 "url": "https://astralight.xyz/compatibilite-astrologique"
    //             },
    //             {
    //                 "@type": "ListItem",
    //                 "position": 3,
    //                 "name": "Compatibilité Gémeaux et Balance",
    //                 "url": "https://astralight.xyz/compatibilite-astrologique"
    //             },
    //             {
    //                 "@type": "ListItem",
    //                 "position": 4,
    //                 "name": "Compatibilité Scorpion et Poissons",
    //                 "url": "https://astralight.xyz/compatibilite-astrologique"
    //             },
    //             {
    //                 "@type": "ListItem",
    //                 "position": 5,
    //                 "name": "Compatibilité Verseau et Sagittaire",
    //                 "url": "https://astralight.xyz/compatibilite-astrologique"
    //             }
    //         ]
    //     };
    // };

    // Ajoutez ces refs
    const suggestionTimeoutRef1 = useRef(null);
    const suggestionTimeoutRef2 = useRef(null);

    // Fonction pour rechercher des villes avec Nominatim (comme dans Astra.js)
    const searchCities = async (query, personNumber) => {
        console.log(`🔍 Recherche de villes pour personne ${personNumber}:`, query);
        if (query.length < 3) {
            console.log('❌ Requête trop courte, minimum 3 caractères');
            if (personNumber === 1) {
                setCitySuggestions1([]);
                setShowSuggestions1(false);
            } else {
                setCitySuggestions2([]);
                setShowSuggestions2(false);
            }
            return;
        }

        try {
            const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=5&featuretype=city`;
            console.log('📡 Appel API Nominatim:', url);

            const response = await fetch(url);
            const data = await response.json();
            console.log('📥 Données reçues de Nominatim:', data);

            const formattedSuggestions = data.map(item => ({
                display: `${item.name}, ${item.display_name.split(', ').slice(1).join(', ')}`,
                lat: item.lat,
                lon: item.lon,
                name: item.name
            }));

            console.log('✨ Suggestions formatées:', formattedSuggestions);
            if (personNumber === 1) {
                setCitySuggestions1(formattedSuggestions);
                setShowSuggestions1(true);
            } else {
                setCitySuggestions2(formattedSuggestions);
                setShowSuggestions2(true);
            }
        } catch (error) {
            console.error('❌ Erreur lors de la recherche des villes:', error);
        }
    };

    // Gestion du changement de champ pour une personne spécifique
    const handlePersonChange = (e, personNumber) => {
        const { name, value } = e.target;
        const personKey = `person${personNumber}`;

        setFormData({
            ...formData,
            [personKey]: {
                ...formData[personKey],
                [name]: value
            }
        });
    };

    // Gestion de la recherche de ville
    const handleCitySearch = (e, personNumber) => {
        const value = e.target.value;
        
        if (personNumber === 1) {
            setCitySearch1(value);
            
            // Debounce la recherche pour personne 1
            if (suggestionTimeoutRef1.current) {
                clearTimeout(suggestionTimeoutRef1.current);
            }
            
            suggestionTimeoutRef1.current = setTimeout(() => {
                searchCities(value, 1);
            }, 300);
        } else {
            setCitySearch2(value);
            
            // Debounce la recherche pour personne 2
            if (suggestionTimeoutRef2.current) {
                clearTimeout(suggestionTimeoutRef2.current);
            }
            
            suggestionTimeoutRef2.current = setTimeout(() => {
                searchCities(value, 2);
            }, 300);
        }
    };

    // Sélection d'une ville dans les suggestions
    const handleCitySelect = (suggestion, personNumber) => {
        const personKey = `person${personNumber}`;
        
        setFormData({
            ...formData,
            [personKey]: {
                ...formData[personKey],
                city: suggestion.name,
                latitude: suggestion.lat,
                longitude: suggestion.lon
            }
        });

        if (personNumber === 1) {
            setCitySearch1(suggestion.display);
            setShowSuggestions1(false);
        } else {
            setCitySearch2(suggestion.display);
            setShowSuggestions2(false);
        }
    };

    // Soumission du formulaire
    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoadingState('loading');
        setError(null);

        try {
            console.log('📤 Envoi des données au backend:', formData);
            const response = await api.post('/calculate_compatibility', formData);
            console.log('📥 Réponse du backend:', response.data);

            if (response.data.success) {
                setCompatibilityData(response.data.data);
                setLoadingState('success');
            } else {
                setError(response.data.error || 'Une erreur est survenue lors du calcul de compatibilité.');
                setLoadingState('error');
            }
        } catch (error) {
            console.error('❌ Erreur lors de la soumission du formulaire:', error);
            setError(error.response?.data?.error || 'Une erreur est survenue lors de la communication avec le serveur.');
            setLoadingState('error');
        }
    };

    // Rendu du formulaire pour une personne
    const renderPersonForm = (personNumber) => {
        const personKey = `person${personNumber}`;
        const personData = formData[personKey];
        const citySearch = personNumber === 1 ? citySearch1 : citySearch2;
        const suggestions = personNumber === 1 ? citySuggestions1 : citySuggestions2;
        const showSuggestions = personNumber === 1 ? showSuggestions1 : showSuggestions2;

        return (
            <div className="person-form">
                <h3>Personne {personNumber}</h3>
                <div className="form-group">
                    <label htmlFor={`${personKey}-birth_date`}>Date de naissance</label>
                    <input
                        type="date"
                        id={`${personKey}-birth_date`}
                        name="birth_date"
                        value={personData.birth_date}
                        onChange={(e) => handlePersonChange(e, personNumber)}
                        required
                    />
                </div>
                <div className="form-group">
                    <label htmlFor={`${personKey}-birth_time`}>Heure de naissance</label>
                    <input
                        type="time"
                        id={`${personKey}-birth_time`}
                        name="birth_time"
                        value={personData.birth_time}
                        onChange={(e) => handlePersonChange(e, personNumber)}
                        required
                    />
                </div>
                <div className="form-group">
                    <label htmlFor={`${personKey}-city`}>Lieu de naissance</label>
                    <div className="city-search-container">
                        <input
                            className="city-input"
                            type="text"
                            id={`${personKey}-city`}
                            value={citySearch}
                            onChange={(e) => handleCitySearch(e, personNumber)}
                            placeholder="Commencez à taper le nom d'une ville..."
                            required
                        />
                        {showSuggestions && suggestions.length > 0 && (
                            <ul className="city-suggestions">
                                {suggestions.map((suggestion, index) => (
                                    <li
                                        key={index}
                                        onClick={() => handleCitySelect(suggestion, personNumber)}
                                    >
                                        {suggestion.display}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    // Fonction pour afficher un score sous forme d'étoiles
    const renderScoreStars = (score) => {
        const maxScore = 10;
        const stars = [];
        
        for (let i = 1; i <= maxScore; i++) {
            if (i <= score) {
                stars.push(<span key={i} className="star filled">★</span>);
            } else {
                stars.push(<span key={i} className="star">☆</span>);
            }
        }
        
        return <div className="score-stars">{stars}</div>;
    };

    // Rendu des résultats de compatibilité
    const renderCompatibilityResults = () => {
        if (!compatibilityData || !compatibilityData.compatibility) return null;
        
        const { compatibility, person1, person2 } = compatibilityData;
        const analysis = compatibility.compatibility_analysis;
        
        // Fonction utilitaire pour créer des liens vers les planètes dans l'affichage des résultats
        const getPlanetLink = (astreName, plural = false) => {
            const planetMap = {
                'Soleil': '/blog/planetes/soleil',
                'Lune': '/blog/planetes/lune',
                'Mercure': '/blog/planetes/mercure',
                'Vénus': '/blog/planetes/venus',
                'Mars': '/blog/planetes/mars',
                'Jupiter': '/blog/planetes/jupiter',
                'Saturne': '/blog/planetes/saturne',
                'Uranus': '/blog/planetes/uranus',
                'Neptune': '/blog/planetes/neptune',
                'Pluton': '/blog/planetes/pluton',
            };
            
            const baseName = astreName.replace(/s$/, ''); // Enlève le 's' éventuel
            const displayName = plural ? `${baseName}s` : astreName;
            
            if (planetMap[baseName]) {
                return <Link to={planetMap[baseName]}>{displayName}</Link>;
            }
            
            return displayName;
        };
        
        return (
            <div className="compatibility-results panel">
                <h2>Résultats de Compatibilité</h2>
                
                <div className="persons-info">
                    <div className="person-info">
                        <h3>Personne 1</h3>
                        <p><strong>Date de naissance:</strong> {person1.identity.birth_date}</p>
                        <p><strong>Heure de naissance:</strong> {person1.identity.birth_time}</p>
                        <p><strong>Lieu de naissance:</strong> {person1.identity.birth_location.city}</p>
                    </div>
                    <div className="person-info">
                        <h3>Personne 2</h3>
                        <p><strong>Date de naissance:</strong> {person2.identity.birth_date}</p>
                        <p><strong>Heure de naissance:</strong> {person2.identity.birth_time}</p>
                        <p><strong>Lieu de naissance:</strong> {person2.identity.birth_location.city}</p>
                    </div>
                </div>
                
                <div className="astrological-info">
                    <div className="person-astrological-info">
                        <h3>Thème de Personne 1</h3>
                        <p>Pour une analyse complète du profil astrologique de la Personne 1, vous pouvez calculer son <Link to="/calcul-theme-astral">thème astral individuel</Link>.</p>
                        <table className="astro-table">
                            <thead>
                                <tr>
                                    <th>Corps céleste</th>
                                    <th>Signe</th>
                                    <th>Position</th>
                                </tr>
                            </thead>
                            <tbody>
                                {person1.raw_data.positions_astres.map((astre, index) => (
                                    <tr key={index}>
                                        <td>{getPlanetLink(astre.astre)}</td>
                                        <td>{astre.signe}</td>
                                        <td>{astre.longitude}° (absolus) / {astre.degre_relatif}° (relatifs)</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    
                    <div className="person-astrological-info">
                        <h3>Thème de Personne 2</h3>
                        <p>Pour une analyse complète du profil astrologique de la Personne 2, vous pouvez calculer son <Link to="/calcul-theme-astral">thème astral individuel</Link>.</p>
                        <table className="astro-table">
                            <thead>
                                <tr>
                                    <th>Corps céleste</th>
                                    <th>Signe</th>
                                    <th>Position</th>
                                </tr>
                            </thead>
                            <tbody>
                                {person2.raw_data.positions_astres.map((astre, index) => (
                                    <tr key={index}>
                                        <td>{getPlanetLink(astre.astre)}</td>
                                        <td>{astre.signe}</td>
                                        <td>{astre.longitude}° (absolus) / {astre.degre_relatif}° (relatifs)</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
                
                <div className="overall-score section">
                    <h3>Score Global de Compatibilité</h3>
                    <div className="score-container">
                        {renderScoreStars(analysis.final_evaluation.overall_score)}
                        <span className="score-value">{analysis.final_evaluation.overall_score}/10</span>
                    </div>
                </div>
                
                <div className="attraction section">
                    <h3>Attraction Mutuelle</h3>
                    
                    <div className="compatibility-item">
                        <div className="compatibility-header">
                            <h4>Compatibilité des Ascendants</h4>
                            {renderScoreStars(analysis.attraction.ascendants.compatibility_score)}
                        </div>
                        <p>{analysis.attraction.ascendants.description}</p>
                    </div>
                    
                    <div className="compatibility-item">
                        <div className="compatibility-header">
                            <h4>Compatibilité des {getPlanetLink('Soleil', true)}</h4>
                            {renderScoreStars(analysis.attraction.suns.compatibility_score)}
                        </div>
                        <p>{analysis.attraction.suns.description}</p>
                    </div>
                    
                    <div className="compatibility-item">
                        <div className="compatibility-header">
                            <h4>Compatibilité des {getPlanetLink('Lune', true)}</h4>
                            {renderScoreStars(analysis.attraction.moons.compatibility_score)}
                        </div>
                        <p>{analysis.attraction.moons.description}</p>
                    </div>
                </div>
                
                <div className="relationship-dynamics section">
                    <h3>Dynamiques Relationnelles</h3>
                    
                    <div className="compatibility-item">
                        <div className="compatibility-header">
                            <h4>{getPlanetLink('Vénus')} (Amour et Affection)</h4>
                            {renderScoreStars(analysis.relationship_dynamics.venus.compatibility_score)}
                        </div>
                        <p>{analysis.relationship_dynamics.venus.description}</p>
                    </div>
                    
                    <div className="compatibility-item">
                        <div className="compatibility-header">
                            <h4>{getPlanetLink('Mars')} (Passion et Énergie)</h4>
                            {renderScoreStars(analysis.relationship_dynamics.mars.compatibility_score)}
                        </div>
                        <p>{analysis.relationship_dynamics.mars.description}</p>
                    </div>
                </div>
                
                <div className="major-aspects section">
                    <h3>Aspects Majeurs</h3>
                    
                    <div className="compatibility-item">
                        <div className="compatibility-header">
                            <h4>Correspondances avec le Descendant</h4>
                            {renderScoreStars(analysis.major_aspects.descendant_matches.compatibility_score)}
                        </div>
                        <p>{analysis.major_aspects.descendant_matches.description}</p>
                    </div>
                    <div className="compatibility-item">
                        <div className="compatibility-header">
                            <h4>Aspects {getPlanetLink('Pluton')}-{getPlanetLink('Lune')}</h4>
                            {renderScoreStars(analysis.major_aspects.pluto_moon.compatibility_score)}
                        </div>
                        <p>{analysis.major_aspects.pluto_moon.description}</p>
                    </div>
                </div>
                
                <div className="key-challenges section">
                    <h3>Défis Clés</h3>
                    
                    <div className="compatibility-item">
                        <div className="compatibility-header">
                            <h4>Communication</h4>
                            <div className="impact-score">
                                Impact: {analysis.key_challenges.communication.impact_score}/10
                            </div>
                        </div>
                        <p>{analysis.key_challenges.communication.description}</p>
                    </div>
                    
                    <div className="compatibility-item">
                        <div className="compatibility-header">
                            <h4>Besoins Émotionnels</h4>
                            <div className="impact-score">
                                Impact: {analysis.key_challenges.emotional_needs.impact_score}/10
                            </div>
                        </div>
                        <p>{analysis.key_challenges.emotional_needs.description}</p>
                    </div>
                    
                    <div className="compatibility-item">
                        <div className="compatibility-header">
                            <h4>Autonomie vs Fusion</h4>
                            <div className="impact-score">
                                Impact: {analysis.key_challenges.autonomy_vs_fusion.impact_score}/10
                            </div>
                        </div>
                        <p>{analysis.key_challenges.autonomy_vs_fusion.description}</p>
                    </div>
                </div>
                
                <div className="final-evaluation section">
                    <h3>Évaluation Finale</h3>
                    
                    <div className="strengths-challenges">
                        <div className="strengths">
                            <h4>Forces</h4>
                            <ul>
                                {analysis.final_evaluation.strengths.map((strength, index) => (
                                    <li key={index}>{strength}</li>
                                ))}
                            </ul>
                        </div>
                        
                        <div className="challenges">
                            <h4>Défis</h4>
                            <ul>
                                {analysis.final_evaluation.challenges.map((challenge, index) => (
                                    <li key={index}>{challenge}</li>
                                ))}
                            </ul>
                        </div>
                    </div>
                    
                    <div className="relationship-potential">
                        <h4>Potentiel de la Relation</h4>
                        <div className="potential-item">
                            <strong>Court terme:</strong> {analysis.final_evaluation.relationship_potential.short_term}
                        </div>
                        <div className="potential-item">
                            <strong>Long terme:</strong> {analysis.final_evaluation.relationship_potential.long_term}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // Rendu du contenu principal
    const renderContent = () => {
        if (loadingState === 'loading') {
            return <Loader />;
        }

        if (compatibilityData) {
            return renderCompatibilityResults();
        }

        return null;
    };

    // Ajoutez également une gestion des clics en dehors si nécessaire
    useEffect(() => {
        const handleClickOutside = (event) => {
            // Fermer les suggestions si on clique en dehors
            if (!event.target.closest('.city-search-container')) {
                setShowSuggestions1(false);
                setShowSuggestions2(false);
            }
        };
        
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    return (
        <>
            <Helmet>
                <title>Compatibilité Astrologique Couple | Test de Synastrie Gratuit</title>
                <meta name="description" content="Découvrez votre compatibilité astrologique amoureuse avec notre test de synastrie gratuit. Analysez les aspects planétaires pour évaluer l'harmonie de votre couple." />
                
                {/* Balise canonique */}
                <link rel="canonical" href="https://astralight.xyz/compatibilite-amoureuse-astrologie" />
                
                {/* Balises Open Graph pour Facebook */}
                <meta property="og:type" content="website" />
                <meta property="og:url" content="https://astralight.xyz/compatibilite-amoureuse-astrologie" />
                <meta property="og:title" content="Compatibilité Astrologique Couple | Test de Synastrie Gratuit" />
                <meta property="og:description" content="Analysez la compatibilité amoureuse entre deux thèmes astraux avec notre test de synastrie gratuit. Découvrez les forces et défis de votre relation." />
                <meta property="og:image" content="https://astralight.xyz/images/astralight_og.webp" />
                <meta property="og:site_name" content="AstraLight" />
                
                {/* Twitter Card */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="Compatibilité Astrologique Couple | Test de Synastrie" />
                <meta name="twitter:description" content="Testez la compatibilité astrologique de votre couple: aspects Vénus-Mars, Soleil-Lune et autres indicateurs clés de la synastrie." />
                <meta name="twitter:image" content="https://astralight.xyz/images/astralight_og.webp" />
                
                {/* Autres balises méta importantes */}
                <meta name="robots" content="index, follow" />
                <meta name="keywords" content="compatibilité astrologique, synastrie, test compatibilité couple, compatibilité signe astrologique, aspects vénus mars, compatibilité amoureuse gratuite" />
                <meta name="author" content="AstraLight" />
                
                {/* Schemas structurés */}
                <script type="application/ld+json">
                    {JSON.stringify(getToolSchema())}
                </script>
                {/* <script type="application/ld+json">
                    {JSON.stringify(getPopularPairsSchema())}
                </script> */}
            </Helmet>
            
            <Navbar />
            
            <div className="container compatibility-container">
                <header className="compatibility-header">
                    <h1>Compatibilité Astrologique</h1>
                    <p>Découvrez la compatibilité astrologique, où les étoiles et les planètes influencent les relations entre deux personnes. En analysant les thèmes astraux, nous mettons en lumière les points forts et les défis qui peuvent affecter leur lien. Explorez les affinités et les obstacles qui peuvent se présenter sur leur chemin.</p>
                </header>

                {/* Insérer le contenu informatif sur la synastrie avant le formulaire */}
                <SynastrieInfoContent />

                {!guid && !compatibilityData && (
                    <div className="compatibility-form-container panel">
                        <h2>Entrez les informations de naissance</h2>
                        <form onSubmit={handleSubmit}>
                            <div className="dual-form">
                                {renderPersonForm(1)}
                                {renderPersonForm(2)}
                            </div>
                            <button type="submit" className="submit-button" disabled={loadingState === 'loading'}>
                                {loadingState === 'loading' ? 'Calcul en cours...' : 'Calculer la compatibilité'}
                            </button>
                        </form>
                        {error && <div className="error-message">{error}</div>}
                    </div>
                )}

                {renderContent()}
                
                {/* Sections SEO toujours visibles */}
                <hr />
                <ScoreGuide />
                <FAQ />
            </div>
        </>
    );
};

export default Compatibility; 